import React from "react";

import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";

import { makeStyles } from "@mui/styles";

import { ObjectType } from "../../types/tableTypes";
import InforErrorIcon from "../../assets/InfoErrorIcon.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';

import { isFalsyButNotZero } from "../../utils/Validations";

import "./CustomCard.css";
import { getCookie } from "../../utils/Common";
import WaiverRequestForm from "../waivers/WaiverRequestForm";
import WaiverSummary from "../waivers/WaiverSummary";
import { findTestWaiver, WaiverServiceRecord } from "../waivers/utils";

const useStyles = makeStyles({
  card: {
    alignItems: "center",
    alignContent: "center",
    padding: "0px",
    borderRadius: "6px",
    background: "#FFF",
    boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.14)",
  },
  testCard: {
    borderRadius: "6px !important;",
    backgroundColor: "#F7F7F7 !important;",
  },
});

const EMAIL_BOUNDARY_CHARS = new Set([" ", "\t", "\n", "\r", "\f", "\v", " ", "@"]);

// Finds and wraps the first "local@domain.tld"-shaped substring in text as a
// mailto link, using plain index scanning instead of a regex. A
// backtracking-based pattern here (e.g. /[^\s@]+@[^\s@]+\.[^\s@]+/) keeps
// getting flagged by static analysis as super-linear regardless of how the
// character classes are shaped, since the domain-label class unavoidably
// overlaps the literal "." that follows it. Scanning by index has no
// backtracking to exploit at all.
function linkifyFirstEmail(text: string): string {
  const at = text.indexOf("@");
  if (at <= 0 || at >= text.length - 1) return text;

  let start = at;
  while (start > 0 && !EMAIL_BOUNDARY_CHARS.has(text[start - 1])) start--;

  let end = at + 1;
  while (end < text.length && !EMAIL_BOUNDARY_CHARS.has(text[end])) end++;

  const dot = text.lastIndexOf(".", end - 1);
  const hasDomainLabel = dot > at + 1;
  const hasTld = dot !== -1 && dot < end - 1;
  if (!hasDomainLabel || !hasTld) return text;

  const email = text.slice(start, end);
  return `${text.slice(0, start)}<a href="mailto:${email}">${email}</a>${text.slice(end)}`;
}

export default function CustomCard({
  headingText,
  subHeadingText,
  loanDetails,
  subComponents,
  fullView,
  ratesAsOfDate,
  data,
  gridSpace,
  cardCss,
  headerClass,
  showFailed = false,
  background,
  icon,
  textMaxWidth,
  showRequestWaiver = false,
  canRequestWaiver = false,
  canActOnWaiver = false,
  onRequestWaiverClick,
  currentTabResults,
  currentTabKey,
  testType,
  testKey,
  isWaiverFormOpen = false,
  onWaiverSubmit,
  onWaiverCancel,
  headingActions,
}: {
  headingText: any;
  subHeadingText: string;
  loanDetails: ObjectType;
  fullView: boolean;
  ratesAsOfDate: string | undefined;
  subComponents?: any;
  data?: ObjectType;
  gridSpace?: number;
  cardCss?: boolean;
  headerClass?: string;
  showFailed?: boolean;
  background?: string;
  icon?: any;
  textMaxWidth?: any;
  showRequestWaiver?: boolean;
  canRequestWaiver?: boolean;
  canActOnWaiver?: boolean;
  onRequestWaiverClick?: (props: any) => void;
  currentTabResults?: any;
  currentTabKey?: string;
  testType?: string;
  testKey?: string;
  isWaiverFormOpen?: boolean;
  onWaiverSubmit?: (data: WaiverServiceRecord) => void;
  onWaiverCancel?: () => void;
  // Rendered as a sibling of the <h3> heading, inside the same headerClass
  // row — e.g. "div-space-between" (App.css) puts it flush right on the
  // same line as the title. Only ever passed by the Pricing adjustments
  // card today (its edit/save/cancel icons); nothing else uses it, so every
  // other CustomCard caller is unaffected by omitting it.
  headingActions?: any;
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const newRatesAsOfDate = loanDetails?.tcLoanId
    ? loanDetails?.ratesAsOf
    : ratesAsOfDate || loanDetails?.ratesAsOf;

  const orgFundingType =
    loanDetails?.fundingType || getCookie("orgFundingType");

  let finalRateSubText: string = t("finalRateSubText");
  finalRateSubText = linkifyFirstEmail(finalRateSubText);

  const IconToBeShown = icon ? icon : InforErrorIcon;

  const { loanId } = useParams<{ loanId?: string }>();

  const existingWaiver = findTestWaiver(
    loanDetails?.waiverDetails,
    loanId,
    testType,
    testKey,
  );

  function getValue(value: any, type: string) {
    if (type === "percent") {
      return value ? `${value} %` : "0.00 %";
    }
  }

  // Below, the original CustomCard render tree is split into a set of
  // sibling helper functions (one per cohesive chunk of markup/logic),
  // each closing over the props/state above. This is a purely structural
  // extraction to keep the component's own cognitive complexity low —
  // every helper is called from renderCardBody()/the final return exactly
  // where its markup used to sit inline, with identical conditions and
  // JSX copied over verbatim.

  function getHeadingClassName() {
    return ["Final Price", "Proceeds"].includes(headingText)
      ? "headerText"
      : "headerText align-text-center";
  }

  function getHeadingStyle(): React.CSSProperties {
    return showFailed
      ? { textAlign: "start", margin: "4px", paddingBottom: "16px" }
      : { margin: "4px", justifyContent: "center" };
  }

  function renderHeadingIcon() {
    return showFailed ? (
      <img
        src={IconToBeShown}
        alt="info-error.svg"
        className="mr-24"
      />
    ) : null;
  }

  function renderFinalPriceRate() {
    return data?.finalPriceRate ? (
      <span className="final-rate-price-text">
        {isFalsyButNotZero(data?.finalPriceRate)
          ? `${data?.finalPriceRate} %`
          : ""}
      </span>
    ) : null;
  }

  function renderRatesAsOfBlock() {
    return ["Final Price"].includes(headingText) && loanDetails?.ratesAsOf ? (
      <div className="mt-16">
        <span className="rates-text">
          {t("ratesAsOfDate")} &nbsp;
          <b>
            {moment(newRatesAsOfDate, "DD-MM-YYYY").format(
              "MMM DD, YYYY"
            )}
          </b>
        </span>
      </div>
    ) : null;
  }

  function getSubHeadingContainerStyle(): React.CSSProperties {
    return typeof headingText === "string" ? {
      display: "flex",
      justifyContent: "center",
      flexDirection: "column"
    } : { maxWidth: textMaxWidth ?? subHeadingText === "Final Buy Rate" ? "auto" : "", display: "flex", justifyContent: "center", flexDirection: "column" };
  }

  function getSubHeadingClassName() {
    return `subHeadingText ${
      showFailed ? "text-align-left" : ""
    }`;
  }

  function renderLeverageAdjustmentBlock() {
    return data?.isLeverageAdjustment ? (
      <div className="mb-12 w-100">
        <Grid container className="mb-12">
          <Grid item xs={6}>
            <span
              className={
                data?.totalAdjustments < 0
                  ? "error-text fs-20"
                  : "fs-20"
              }
            >
              {getValue(
                data?.totalAdjustments?.toFixed(2),
                "percent"
              )}
            </span>
            <br />
            <br />
            {t("totalAdjustments")}
          </Grid>
          <Grid item xs={6}>
            <span>{t("totalNegative")}</span> &ensp;
            <span
              className={
                data?.totalAdjustments < 0 ? "error-text" : ""
              }
            >
              {getValue(data?.totalNegative?.toFixed(4), "percent")}
            </span>
            <br />
            <span>{t("totalPositive")}</span> &ensp;
            <span>{getValue(data.totalPositive, "percent")}</span>
          </Grid>
        </Grid>
        <Divider />
      </div>
    ) : null;
  }

  function renderNoteRateInfo() {
    return subHeadingText === "Note Rate (%)" &&  orgFundingType == "table_funding" ? (
        <div style={{ background: "#00000005", display: 'flex', marginTop: "4px" }}>
          <InfoOutlinedIcon
            style={{ fontSize: "14px", marginTop: "2px" }}
          />
          &nbsp;
          <span style={{ fontSize: "12px" }}>
            {t("noteRateSubText")}
          </span>
        </div>
    ) : null;
  }

  function renderFinalBuyRateInfo() {
    return data?.finalRate &&
    subHeadingText === "Final Buy Rate" &&
    orgFundingType == "table_funding" ? (
      <div style={{ background: "#00000005", display: 'flex' }}>
        <InfoOutlinedIcon
          style={{ fontSize: "14px", marginTop: "2px" }}
        />
        &nbsp;
        <div
          dangerouslySetInnerHTML={{ __html: finalRateSubText }}
          style={{ fontSize: "12px", lineBreak: "anywhere" }}
        />
      </div>
    ) : null;
  }

  function renderSubHeadingSection() {
    return subHeadingText || data?.isLeverageAdjustment ? (
      <div
        style={getSubHeadingContainerStyle()}
      >
        <span
          className={getSubHeadingClassName()}
          style={{ margin: "0px", textAlign: 'center' }}
        >
          {subHeadingText}
        </span>
        {renderLeverageAdjustmentBlock()}
        {renderNoteRateInfo()}
        {renderFinalBuyRateInfo()}
      </div>
    ) : null;
  }

  function renderWaiverRequestButton() {
    return showRequestWaiver && canRequestWaiver && !existingWaiver && !isWaiverFormOpen ? (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          marginTop: "16px",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ChecklistOutlinedIcon fontSize="small" />}
          onClick={() => onRequestWaiverClick?.({ headingText, currentTabResults, currentTabKey })}
          data-testid="request-waiver-button"
        >
          {t("requestWaiver")}
        </Button>
      </div>
    ) : null;
  }

  function renderWaiverSection() {
    return showRequestWaiver && existingWaiver ? (
      <WaiverSummary waiver={existingWaiver} canActOnWaiver={canActOnWaiver} />
    ) : showRequestWaiver && isWaiverFormOpen ? (
      <WaiverRequestForm
        currentTabKey={currentTabKey}
        testTitle={headingText}
        testType={testType}
        testKey={testKey}
        onSubmit={onWaiverSubmit}
        onCancel={onWaiverCancel}
      />
    ) : null;
  }

  function renderCardBody() {
    return (
      <div>
        <div className={headerClass} style={{ marginBottom: "4px" }}>
          <h3
            data-testid="custom-card-heading"
            className={getHeadingClassName()}
            style={getHeadingStyle()}
          >
            {renderHeadingIcon()}
            {headingText}
          </h3>
          {headingActions}
          {renderFinalPriceRate()}
        </div>
        {renderRatesAsOfBlock()}
        {renderSubHeadingSection()}
        {subComponents}
        {renderWaiverRequestButton()}
        {renderWaiverSection()}
      </div>
    );
  }

  return (
    // <Grid item xs={fullView ? 12 : gridSpace} style={{'border': '1px solid green'}}>
    <Card
      className={classes.card}
      sx={
        showFailed
          ? {
              marginBottom: "12px",
              background: background ?? "#F7F7F7",
            }
          : {}
      }
      data-testid="custom-card-div"
    >
      <CardContent
        style={{
          height: typeof headingText === "string" ? "auto" : "",
          display: typeof headingText === "string" ? "block" : "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          padding: "12px 14px",
          background: "#fff",
        }}
      >
        {headingText === "Leverage Adjustment" &&
        !data?.isLeverageAdjustment ? (
          <h3 className="noAdjustementText align-text-center" style={{textAlign: 'center'}}>
            {t("noLeveragesAdjustments")}
          </h3>
        ) : (
          renderCardBody()
        )}
      </CardContent>
    </Card>
    // </Grid>
  );
}
